
import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const SIDEBAR_STATE_KEY = 'sidebarExpanded';

const menuItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/" },
  { icon: Users, label: "Clients", path: "/clients" },
  { icon: FolderKanban, label: "Programs", path: "/programs" },
  { icon: Settings, label: "Settings", path: "/settings" },
];

export function Sidebar() {
  const [expanded, setExpanded] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_STATE_KEY);
    return saved !== null ? JSON.parse(saved) : true;
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify(expanded));
  }, [expanded]);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate("/auth");
      toast({
        title: "Signed out successfully",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error signing out",
        description: error.message,
      });
    }
  };

  return (
    <div
      className={cn(
        "h-full bg-white/5 backdrop-blur-lg border-r border-white/10 px-4 transition-all duration-300 flex-shrink-0 rounded-tr-2xl",
        expanded ? "w-64" : "w-20"
      )}
    >
      <div className="flex flex-col h-full py-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setExpanded(!expanded)}
          className="ml-auto mb-4 text-white/70 hover:text-white hover:bg-white/10"
        >
          {expanded ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>

        <nav className="flex-1 space-y-2">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                "flex items-center justify-center px-3 py-2 rounded-lg transition-colors text-white/70 hover:text-white hover:bg-white/10",
                expanded && "justify-start",
                location.pathname === item.path && "bg-white/10 text-white"
              )}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {expanded && <span className="ml-3">{item.label}</span>}
            </Link>
          ))}
        </nav>

        <div className="border-t border-white/10 pt-4">
          <button
            onClick={handleSignOut}
            className={cn(
              "flex items-center justify-center w-full px-3 py-2 rounded-lg transition-colors text-red-400 hover:text-red-300 hover:bg-red-400/10",
              expanded && "justify-start"
            )}
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            {expanded && <span className="ml-3">Sign out</span>}
          </button>
        </div>
      </div>
    </div>
  );
}
